import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Profile } from '@modelcast/types';
import { useAuth } from '../auth';
import { authHeaders } from './authHeaders';

export type CurrentProfileStatus = 'loading' | 'signed-out' | 'no-profile' | 'ready' | 'error';

interface CurrentProfileContextValue {
  profile: Profile | null;
  defaultEventCurrency: string | null;
  isLoading: boolean;
  status: CurrentProfileStatus;
  refresh: () => Promise<void>;
}

interface CurrentProfileResponse {
  profile?: Profile | null;
  defaultEventCurrency?: unknown;
}

const CurrentProfileContext = createContext<CurrentProfileContextValue | null>(null);

export function CurrentProfileProvider({ children }: { children: ReactNode }) {
  const { getToken, isLoaded: authLoaded, isSignedIn, userId } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [defaultEventCurrency, setDefaultEventCurrency] = useState<string | null>(null);
  const [status, setStatus] = useState<CurrentProfileStatus>('loading');
  const authRef = useRef({ authLoaded, getToken, isSignedIn, userId });
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    authRef.current = { authLoaded, getToken, isSignedIn, userId };
  }, [authLoaded, getToken, isSignedIn, userId]);

  const loadProfile = useCallback(async (clearExisting: boolean) => {
    const requestGeneration = ++requestGenerationRef.current;
    const auth = authRef.current;

    if (!auth.authLoaded) {
      setProfile(null);
      setDefaultEventCurrency(null);
      setStatus('loading');
      return;
    }

    if (!auth.isSignedIn || !auth.userId) {
      setProfile(null);
      setDefaultEventCurrency(null);
      setStatus('signed-out');
      return;
    }

    if (clearExisting) {
      setProfile(null);
      setDefaultEventCurrency(null);
    }
    setStatus('loading');

    try {
      const token = await auth.getToken();
      const response = await fetch('/api/profiles/me', {
        headers: authHeaders(token),
      });

      if (requestGeneration !== requestGenerationRef.current) return;

      if (response.status === 404) {
        setProfile(null);
        setDefaultEventCurrency(null);
        setStatus('no-profile');
        return;
      }

      if (!response.ok) {
        setProfile(null);
        setDefaultEventCurrency(null);
        setStatus('error');
        return;
      }

      const data = await response.json() as CurrentProfileResponse;
      if (requestGeneration !== requestGenerationRef.current) return;

      const nextProfile = data.profile ?? null;
      setProfile(nextProfile);
      setDefaultEventCurrency(
        typeof data.defaultEventCurrency === 'string' ? data.defaultEventCurrency : null,
      );
      setStatus(nextProfile ? 'ready' : 'no-profile');
    } catch {
      if (requestGeneration === requestGenerationRef.current) {
        setProfile(null);
        setDefaultEventCurrency(null);
        setStatus('error');
      }
    }
  }, []);

  const refresh = useCallback(() => loadProfile(false), [loadProfile]);

  useEffect(() => {
    void loadProfile(true);

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [authLoaded, isSignedIn, loadProfile, userId]);

  const value = useMemo<CurrentProfileContextValue>(() => ({
    profile,
    defaultEventCurrency,
    isLoading: status === 'loading',
    status,
    refresh,
  }), [defaultEventCurrency, profile, refresh, status]);

  return (
    <CurrentProfileContext.Provider value={value}>
      {children}
    </CurrentProfileContext.Provider>
  );
}

export function useCurrentProfile() {
  const context = useContext(CurrentProfileContext);
  if (!context) {
    throw new Error('useCurrentProfile must be used inside CurrentProfileProvider');
  }
  return context;
}
